/**
 * SA-459 (workflow UX sweep spec §3.4, §6, D3): a step's card as a
 * drawer over the page, 540px until the viewer drags its left edge (SA-1077) — the header (eyebrow, `N Title`, the who-chips),
 * the six facts, the executor detail, a footer with the page's own actions
 * and Close. The template half comes from the definition through
 * `projectStepDetail`; the live half ("On this <noun>") is SA-460's. Built
 * on `components/ui/Drawer` (Radix: focus trap, Esc, overlay click, and
 * focus back to the card on close). Nothing here writes. SA-796 removed the
 * footer sentence and the entry step's "born complete" sentence.
 */
import { useId, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, Lock } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Badge, Button, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui'
import { cn } from '@/utils'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { useDrawerWidth } from '@/hooks/useDrawerWidth'
import {
  fieldLabelFor,
  projectStepDetail,
  type ProcessOverviewVM,
  type ProcessStepVM,
  type StepDetailField,
  type StepDetailFormField,
  type StepDetailVM,
} from '@/utils/workflowOverview'
import type { OrderEventItem } from '@/types/commerce'
import type { ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import { FactList, type Fact } from '@/components/records/FactList'
import { SourceLink } from '@/components/records/SourceLink'
import { sourceLabel, type SourceRef } from '@/components/records/sourceText'
import { hueClasses } from './executorTone'
import { FieldValue } from './FieldValue'
import { describeProcessEvent, type ProcessEventText } from './processEventText'
import { RESTRICTED_VALUE } from './restrictedText'
import type { LiveField, StepLiveVM } from './stepLive'
import { kindWord, statusLine, taskLine, whenText } from './stepLiveText'
import { fromHere, taskLinkState, taskTarget } from './taskLink'
import { LEVEL_SENTENCE, exitWord, whoChips, type WhoChip } from './stepDrawerText'
import { AiTold } from './AiTold'

export interface StepDrawerProps {
  vm: ProcessOverviewVM
  definition: ProcessWorkflowDefinitionJson
  /** `null` closes; an id the definition lacks renders nothing. */
  stepId: string | null
  onClose: () => void
  /** The eyebrow's version, when known ("v3"). */
  versionLabel?: string | null
  /** The page's own footer actions (spec §6.4); the drawer adds Close. */
  actions?: ReactNode
  /** The record noun, lowercased, for the live half's "On this <noun>". */
  noun?: string
  /** SA-460 (spec §6.3): the record half, "On this <noun>". Absent on the workflow page. */
  live?: StepLiveVM | null
  /** Re-open a completed task: the host opens the core dialog; the drawer never writes. */
  onReopen?: (task: { id: string; stepTitle: string }) => void
  /** The record's source, for the conversation step's original-enquiry link
   * (spec §6.2; carried here until SA-461's strip). */
  source?: SourceRef | null
  /** SA-469 (spec §3): the record page's birth line for the entry step. Absent on the
   * workflow page (the block then reads in the present tense); `line` null while loading. */
  birth?: { line: string | null } | null
  /** SA-796 (spec D1, D3): the record lane's facts — Step and Due, the other four behind a
   * remembered "Step details" disclosure. Absent on the workflow page: all six, as SA-459 drew them. */
  collapsibleFacts?: boolean
}

/** The `.k` register: the facts' `<dt>`s and the level word. */
const KEY = 'text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground'

function Chip({ chip }: { chip: WhoChip }) {
  if (chip.hue === null) return <span className="text-xs text-muted-foreground">{chip.text}</span>
  const { chip: wash, text } = hueClasses(chip.hue)
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full px-2 text-[11.5px] font-medium leading-5',
        chip.outline ? `border border-current bg-transparent ${text}` : wash,
      )}
    >
      {chip.text}
    </span>
  )
}

function FieldPill({ field }: { field: StepDetailField }) {
  return (
    <span
      title={field.label}
      className="inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-md border border-primary/20 bg-primary/10 px-1.5 text-[11px] font-medium leading-5"
    >
      {field.restricted && <Lock role="img" aria-label="restricted" className="size-3" />}
      <span className="truncate">{field.label}</span>
    </span>
  )
}

function Pills({ fields, empty }: { fields: StepDetailField[]; empty: string }) {
  if (!fields.length) return <span className="text-xs text-muted-foreground">{empty}</span>
  return (
    <span className="flex flex-wrap gap-1">
      {fields.map((f) => (
        <FieldPill key={f.id} field={f} />
      ))}
    </span>
  )
}

function FormRow({ field }: { field: StepDetailFormField }) {
  const kind = field.options ? field.options.join(' / ') : field.columns ? field.columns.map((c) => c.label).join(' · ') : field.type
  return (
    <li className="flex flex-wrap items-baseline gap-2 rounded-md border border-border px-2.5 py-1.5 text-[13px]">
      <span className="font-medium">{field.label}</span>
      <span className="text-xs text-muted-foreground">{kind}</span>
      {field.optional && <span className="text-xs text-muted-foreground">optional</span>}
      {field.restricted && (
        <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-1.5 text-[11px] text-muted-foreground">
          <Lock aria-hidden className="size-3" />
          restricted
        </span>
      )}
    </li>
  )
}

function Sect({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  )
}

/** SA-466: the lane's one fact row. Kept as a tuple-taking wrapper so the
 * call sites below read as prose; the markup lives in `FactList`. */
function Kv({ rows }: { rows: [string, ReactNode][] }) {
  return <FactList facts={rows.map(([k, v]): Fact => ({ key: k, label: k, value: v }))} />
}

/** The facts' grid: an 84px label column, so two lists of rows line up as one. */
const FACTS_GRID = 'grid grid-cols-[84px_minmax(0,1fr)] gap-3'

/** One fact: a `dt`/`dd` pair as a fragment, so both stay direct children of their `<dl>`. */
function FactRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className={cn(KEY, 'pt-[3px]')}>{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  )
}

/** SA-796 (spec D2): the "Step details" memory, per viewer and device (`RecordSection`'s precedent). */
const STEP_DETAILS_KEY = 'step-drawer:details'

function readDetailsOpen(): boolean {
  try {
    return window.localStorage.getItem(STEP_DETAILS_KEY) === 'open'
  } catch {
    // A private window or blocked site data: collapsed.
    return false
  }
}

function writeDetailsOpen(open: boolean): void {
  try {
    window.localStorage.setItem(STEP_DETAILS_KEY, open ? 'open' : 'closed')
  } catch {
    // Not remembered; the facts still toggle.
  }
}

/** SA-796 (spec §3.1): Step and Due, then the disclosure, then the other four beneath it. */
function CollapsibleFacts({ head, details }: { head: ReactNode; details: ReactNode }) {
  const [open, setOpen] = useState(readDetailsOpen)
  const regionId = useId()
  return (
    <div className="flex flex-col gap-3">
      <dl className={FACTS_GRID}>{head}</dl>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => {
          const next = !open
          setOpen(next)
          writeDetailsOpen(next)
        }}
        className="-my-1 inline-flex items-center gap-1 self-start rounded-sm py-1 text-[12.5px] font-medium text-foreground/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronDown aria-hidden className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', !open && '-rotate-90')} />
        Step details
      </button>
      {/* `hidden` on a plain div: the grid `<dl>`'s own `display` could ignore it. */}
      <div id={regionId} hidden={!open}>
        <dl className={FACTS_GRID}>{details}</dl>
      </div>
    </div>
  )
}

/** The six facts (spec §6.2), in the `.k` register. On the workflow page one list in
 * SA-459's order; on a record (SA-796) Step and Due, the other four behind "Step details". */
function Facts({
  step,
  detail,
  definition,
  collapsible,
}: {
  step: ProcessStepVM
  detail: StepDetailVM
  definition: ProcessWorkflowDefinitionJson
  collapsible: boolean
}) {
  const fieldLabel = (id: string) => fieldLabelFor(definition, id)
  const stepRow = (
    <FactRow label="Step">
      <span className="font-semibold">{step.number} {step.title}</span>{' '}
      <span className="text-xs text-muted-foreground">· {detail.laneLabel}</span>
    </FactRow>
  )
  const dueRow = (
    <FactRow label="Due">
      {step.due ?? 'none'}
      {detail.timeoutSentence ? <span className="text-xs text-muted-foreground"> · {detail.timeoutSentence}</span> : null}
    </FactRow>
  )
  const detailRows = (
    <>
      <FactRow label="Who">
        {step.level ? (
          <>
            <span className={KEY}>{step.level}</span>{' '}
            <span className="text-xs text-muted-foreground">· {LEVEL_SENTENCE[step.level] ?? ''}</span>
            {detail.levelsOffered.length > 1 && (
              <div className="text-xs text-muted-foreground">offered: {detail.levelsOffered.join(' · ')}</div>
            )}
          </>
        ) : (
          <span>{step.who}</span>
        )}
      </FactRow>
      <FactRow label="Needs">
        <Pills fields={detail.needs} empty={step.isEntry ? 'nothing' : 'nothing; it starts when the previous step finishes'} />
      </FactRow>
      <FactRow label="Produces">
        <Pills fields={detail.produces} empty="nothing" />
      </FactRow>
      <FactRow label="Next">
        <ul className="space-y-0.5">
          {step.exits.map((e) => (
            <li key={e.name} className="flex flex-wrap items-baseline gap-1.5">
              <span className="font-semibold">{exitWord(e.name)}</span>
              <span aria-hidden="true" className="text-muted-foreground">→</span>
              <span>{e.targetLabel}</span>
              {e.requires.length > 0 && (
                <span className="text-xs text-muted-foreground">· needs {e.requires.map(fieldLabel).join(', ')}</span>
              )}
            </li>
          ))}
        </ul>
      </FactRow>
    </>
  )
  if (collapsible) {
    return <CollapsibleFacts head={<>{stepRow}{dueRow}</>} details={detailRows} />
  }
  return (
    <dl className={FACTS_GRID}>
      {stepRow}
      {detailRows}
      {dueRow}
    </dl>
  )
}

/** The `.sect` blocks (spec §6.2), by executor kind. */
function ExecutorDetail({ step, detail, source, agenda, birth }: { step: ProcessStepVM; detail: StepDetailVM; source?: SourceRef | null; agenda: string; birth?: StepDrawerProps['birth'] }) {
  // SA-459 fix wave item 2: the "exactly what the step produces" sentence
  // is only true when the form IS the produces list, in order — an
  // executor.form naming a subset (or a different order) gets the other
  // sentence instead.
  const formIsProduces =
    detail.form.length === detail.produces.length &&
    detail.form.every((f, i) => f.id === detail.produces[i]?.id)
  return (
    <>
      {(detail.kind === 'person' || detail.kind === 'ai') && (
        <Sect title="Who, and the form">
          <Kv
            rows={[
              ['Seat', detail.seatLabel ?? '—'],
              [
                'Form',
                <span key="form" className="text-xs text-muted-foreground">
                  {formIsProduces
                    ? "The fields are exactly what the step produces; the layout is the platform's."
                    : "The form's fields, in order; the layout is the platform's."}
                </span>,
              ],
            ]}
          />
          {detail.form.length > 0 && (
            <ul className="space-y-1.5">
              {detail.form.map((f) => (
                <FormRow key={f.id} field={f} />
              ))}
            </ul>
          )}
        </Sect>
      )}
      {detail.ai && (
        step.level === 'manual' ? (
          <details className="space-y-2">
            <summary className="cursor-pointer text-[12.5px] font-medium text-foreground/80">What the AI would do at assisted (not turned on)</summary>
            <AiTold ai={detail.ai} />
          </details>
        ) : (
          <Sect title="What the AI is told">
            <AiTold ai={detail.ai} />
          </Sect>
        )
      )}
      {detail.automation && (
        <Sect title="Connector">
          <Kv
            rows={[
              ['Capability', detail.automation.capability],
              ...(detail.automation.operation ? [['Operation', detail.automation.operation] as [string, ReactNode]] : []),
              ['On failure', detail.automation.onFailure],
            ]}
          />
        </Sect>
      )}
      {detail.wait && (
        <Sect title="What it waits for">
          <Kv
            rows={[
              (detail.wait.event ? ['Event', detail.wait.event] : ['Duration', detail.wait.after ?? '—']) as [string, ReactNode],
              ...(detail.wait.timeout ? [['On timeout', detail.wait.timeout] as [string, ReactNode]] : []),
            ]}
          />
        </Sect>
      )}
      {step.isEntry ? (
        // SA-469 (spec D7, §3): the entry step is born complete from the intake on every birth.
        <Sect title={birth ? 'How the enquiry arrived' : 'How the enquiry arrives'}>
          <Kv
            rows={[
              ['Agenda', agenda],
              birth
                ? [
                    'Arrived',
                    <span key="arrived" className="inline-flex flex-wrap items-center gap-1.5">
                      <span>{birth.line ?? '—'}</span>
                      {source && source.kind !== 'none' && (
                        <>
                          <SourceLink source={source} />
                          <span className="text-xs text-muted-foreground">open in the inbox</span>
                        </>
                      )}
                    </span>,
                  ]
                : ['Arrives by', 'an email in a bound mailbox, a pasted enquiry, or a staff conversation'],
            ] as [string, ReactNode][]}
          />
        </Sect>
      ) : detail.conversation && (
        <Sect title="The conversation, by reference">
          <Kv
            rows={[
              ['Agenda', detail.conversation.agendaLabel],
              ...(source && source.kind !== 'none'
                ? [[
                    sourceLabel(source.kind),
                    <span key="source" className="inline-flex items-center gap-1.5">
                      <SourceLink source={source} />
                      <span className="text-xs text-muted-foreground">open in the inbox</span>
                    </span>,
                  ] as [string, ReactNode]]
                : []),
            ]}
          />
        </Sect>
      )}
    </>
  )
}

function RestrictedPill() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-1.5 text-[11px] text-muted-foreground">
      <Lock aria-hidden className="size-3" />
      {RESTRICTED_VALUE}
    </span>
  )
}

/** Label → value rows; a redacted value is the pill, never a blank; a stale value wears its chip. */
function LiveFields({ fields }: { fields: LiveField[] }) {
  return (
    <FactList
      facts={fields.map((f): Fact => ({
        key: f.id,
        label: f.label,
        wide: f.type === 'table',
        value: f.redacted ? <RestrictedPill /> : <FieldValue field={f} />,
        after: f.stale
          ? <span className="ml-1.5 rounded-full bg-warning/10 px-1.5 text-[11px] text-warning-strong">stale</span>
          : undefined,
      }))}
    />
  )
}

/** "On this <noun>" (spec §6.3): status, Recorded, what it saw, the task, the rounds, the ledger. Links to the task page; never a form (D4). */
function LiveBlock({ live, noun, onReopen }: { live: StepLiveVM; noun: string; onReopen?: StepDrawerProps['onReopen'] }) {
  const navigate = useNavigate()
  const location = useLocation()
  const zone = useDefaultTimezone()
  const task = live.task
  // A row is live only while it is open or claimed — a `failed` outbox row is
  // finished, and the block offers View task, never Open task.
  const taskOpen = task ? task.status === 'open' || task.status === 'claimed' : false
  // Only the events this drawer has words for; the block itself renders when
  // at least one survives, so a step whose only event is a bare
  // `process_step_entered` shows no empty Ledger heading.
  const ledger = live.events
    .map((e) => ({ event: e, words: describeProcessEvent(e.eventType, e.payload) }))
    .filter((x): x is { event: OrderEventItem; words: ProcessEventText } => x.words !== null)
  return (
    <section data-testid="step-live" className="space-y-3">
      <h3 className="text-[13px] font-semibold text-foreground">On this {noun}</h3>
      <p className="text-[13px]">
        {statusLine(live.status, new Date(), zone)}
        {live.status.kind === 'current' && live.status.overdue && <Badge variant="destructive" className="ml-2">overdue</Badge>}
      </p>
      {live.recorded.length > 0 && (
        <div className="space-y-1">
          <h4 className={KEY}>Recorded</h4>
          <LiveFields fields={live.recorded} />
        </div>
      )}
      {live.saw && live.saw.length > 0 && (
        <details className="space-y-2">
          <summary className="cursor-pointer text-[12.5px] font-medium text-foreground/80">What this step saw</summary>
          <LiveFields fields={live.saw} />
        </details>
      )}
      {task && (() => {
        const target = taskTarget({ id: task.id, canOpen: task.canOpen, stepId: live.stepId })
        return (
          <div className="space-y-1.5">
            <h4 className={KEY}>Task</h4>
            <p className="text-xs text-muted-foreground">{taskLine(task)}</p>
            <div className="flex flex-wrap items-center gap-2">
              {taskOpen ? (
                target.kind === 'task' && <Button size="sm" onClick={() => navigate(target.to, { state: taskLinkState(fromHere(location)) })}>Open task</Button>
              ) : (
                <>
                  {target.kind === 'task' && <Button size="sm" variant="outline" onClick={() => navigate(target.to, { state: taskLinkState(fromHere(location)) })}>View task</Button>}
                  {task.canReopen && onReopen && (
                    <Button size="sm" variant="ghost" onClick={() => onReopen({ id: task.id, stepTitle: live.stepTitle })}>Re-open</Button>
                  )}
                </>
              )}
            </div>
          </div>
        )
      })()}
      {/* Rounds: the step was visited more than once (a re-open). A row is numbered only when the rows pair one to one with the visits; an assisted step writes an ai row AND a work row per visit, so those rows are named by kind instead; nothing while the history has not arrived. */}
      {live.rounds > 1 && live.history.length > 0 && (
        <div className="space-y-1">
          <h4 className={KEY}>Rounds</h4>
          <ol className="space-y-0.5 text-xs">
            {live.history.map((r, i) => (
              <li key={r.taskId} className="flex flex-wrap items-center gap-x-1.5">
                <span className="font-medium text-foreground">
                  {live.history.length === live.rounds ? `Round ${live.history.length - i}` : kindWord(r.kind)}
                </span>
                {r.exit && <span>· {exitWord(r.exit)}</span>}
                {r.completedByName && <span>· <span>{r.completedByName}</span></span>}
                {r.completedAt && <span className="text-muted-foreground">· {whenText(r.completedAt)}</span>}
                {r.kind !== 'work' && <Badge variant="outline">{r.kind}</Badge>}
              </li>
            ))}
          </ol>
        </div>
      )}
      {ledger.length > 0 && (
        <div className="space-y-1">
          <h4 className={KEY}>Ledger</h4>
          <ul className="space-y-0.5 text-xs">
            {ledger.map(({ event: e, words }) => (
              <li key={e.id} className="flex items-baseline justify-between gap-2">
                <span className="min-w-0">
                  <span className="font-medium text-foreground">{words.label}</span>
                  {words.detail && <span className="text-muted-foreground"> · {words.detail}</span>}
                </span>
                <span className="shrink-0 text-muted-foreground">{whenText(e.occurredAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

/** SA-1077 (SA-813's opt-in): 540 by default (SA-814 ruling 2), the viewer may drag the left edge. */
const STEP_DRAWER_WIDTH = { default: 540, min: 400 }

export function StepDrawer({ vm, definition, stepId, onClose, versionLabel = null, actions, noun = 'record', live = null, onReopen, source = null, birth = null, collapsibleFacts = false }: StepDrawerProps) {
  const resize = useDrawerWidth('step-drawer', STEP_DRAWER_WIDTH, 'Resize step panel')
  const step = stepId ? (vm.steps.find((s) => s.id === stepId) ?? null) : null
  const detail = step ? projectStepDetail(definition, step.id) : null
  const open = step !== null && detail !== null
  // Radix's own `DialogContentModal.onCloseAutoFocus` unconditionally
  // preventDefaults and refocuses `context.triggerRef` — null here, since
  // this drawer is fully controlled (no `Dialog.Trigger`), which would
  // otherwise strand focus on close. Track what had focus just before the
  // drawer opened (read during render, before FocusScope's own mount
  // effect can steal it) and restore it ourselves.
  const wasOpenRef = useRef(false)
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  if (open && !wasOpenRef.current) {
    restoreFocusRef.current = document.activeElement as HTMLElement | null
  }
  wasOpenRef.current = open
  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      {step && detail && (
        <DrawerContent
          resize={resize}
          data-testid="step-drawer"
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            restoreFocusRef.current?.focus()
          }}
        >
          <DrawerHeader className="items-start">
            <div className="min-w-0 space-y-1">
              <p className="text-[11.5px] text-muted-foreground">
                Step card · {vm.name}{versionLabel ? ` ${versionLabel}` : ''}
              </p>
              <DrawerTitle>{step.number} {step.title}</DrawerTitle>
              <div className="flex flex-wrap items-center gap-1.5">
                {whoChips(step, detail).map((c, i) => (
                  <Chip key={i} chip={c} />
                ))}
              </div>
            </div>
          </DrawerHeader>
          <DrawerBody className="space-y-[18px] text-sm">
            <Facts step={step} detail={detail} definition={definition} collapsible={collapsibleFacts} />
            <hr className="border-border" />
            <ExecutorDetail step={step} detail={detail} source={source} agenda={vm.recordAgendaLabel} birth={birth} />
            {live && live.stepId === step.id && (
              <>
                <hr className="border-border" />
                <LiveBlock live={live} noun={noun} onReopen={onReopen} />
              </>
            )}
          </DrawerBody>
          <DrawerFooter className="items-center">
            {actions}
            <Button size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </DrawerFooter>
        </DrawerContent>
      )}
    </Drawer>
  )
}
